import styles from '@/pages/app/Settings/styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Tooltip from '@/components/Tooltip'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { ProductSeatType } from '@/types'
import { AUTOMATIC_SEAT_TYPE_LABEL, seatTypeLabel } from '@/utils/seatType'

// Keep helperText short, the menu sizes to max-content.
const OPTIONS: { id: ProductSeatType; title: string; helperText: string }[] = [
  {
    id: ProductSeatType.Dictation,
    title: seatTypeLabel[ProductSeatType.Dictation],
    helperText: 'New members get Dictation.',
  },
  {
    id: ProductSeatType.Bundle,
    title: seatTypeLabel[ProductSeatType.Bundle],
    helperText: 'New members get Dictation and Notetaker.',
  },
]

const TOOLTIP =
  "The product a new member gets when their invitation doesn't pick one. A seat chosen on an invitation always wins, and IT admins never get a product. Changing this doesn't affect current members, trials or billing."

const getDropdownItems = (current: ProductSeatType | undefined): DropdownItem[] =>
  OPTIONS.map((option) => ({ ...option, selected: option.id === current }))

type Props = {
  /** Undefined means no explicit choice: the server derives the default from the plan. */
  value: ProductSeatType | undefined
  isAdmin: boolean
  isSaving: boolean
  onSelect: (seatType: ProductSeatType) => void
}

/** The organization-wide fallback seat type for future member assignments. */
export default function DefaultSeatTypeSection({ value, isAdmin, isSaving, onSelect }: Props) {
  const handleSelect = (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    const next = selectedItems[0].id as ProductSeatType
    if (next === value) return
    onSelect(next)
  }

  const dropdown = (
    <Dropdown
      items={getDropdownItems(value)}
      onSelect={handleSelect}
      placeholder={AUTOMATIC_SEAT_TYPE_LABEL}
      summary={value === ProductSeatType.NoAccess ? seatTypeLabel[ProductSeatType.NoAccess] : undefined}
      className={styles.localDataPolicyDropdown}
      disabled={!isAdmin || isSaving}
      ariaLabel="Default seat type"
    />
  )

  return (
    <div className={`${styles.settingsItem} ${styles.alignTop}`} data-testid="default-seat-type-setting">
      <div className={styles.labelBlock}>
        <div className={styles.toggleLabel}>
          Default seat type
          <Tooltip text={TOOLTIP} placement="top" hideArrow>
            <InfoIcon className={styles.infoIcon} />
          </Tooltip>
        </div>
        <div className={styles.labelDescription}>
          {value === undefined
            ? 'Automatic: new members get the default for your plan'
            : 'Applies to new members without a seat chosen on their invitation'}
        </div>
      </div>
      <PermissionTooltip>{dropdown}</PermissionTooltip>
    </div>
  )
}
